import { env } from 'process'
import {
  CheckboxField,
  DateField,
  ImageField,
  InputField,
  SelectField,
  TextareaField,
  TimeField,
} from 'components/Inputs/Inputs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import {
  useGetHotelByIdQuery,
  useGetHotelGeneralConfigrationQuery,
} from 'redux/store/hotels/HotelsApi'
import { useToast } from '@chakra-ui/react'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetConfigurationQuery } from 'redux/store/actions'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import Loading from 'components/Loading/Loading'
import {
  useAddTravelsMutation,
  useAddVehiclesMutation,
  useEditTravelsMutation,
  useGetTransportationByIdQuery,
} from 'redux/store/transportations/TransportationsAPI'
import TransportationVehicle from './TransportationVehicle'
import TransportationTravel from './TransportationTravel'

interface GeneralProps {
  setContent: React.Dispatch<React.SetStateAction<string>>
  mode: string
  id: number | null
}

export default function TransportationTravels({ setContent, mode, id }: GeneralProps): JSX.Element {
  const [inActive, setInActive] = useState(false)
  const { t } = useTranslation()
  const toast = useToast()
  const params = useParams()
  const [country, setCountry] = useState<string | number>()
  const [isLoading, setIsLoading] = useState(false)
  const [deletedItems, setDeletedItems] = useState<any[]>([])
  const [addTravels] = useAddTravelsMutation()
  const [editTravels] = useEditTravelsMutation()
  const { data: Cities } = useGetConfigurationQuery(
    country ? `api/Configurations/countries/${country}/cities` : skipToken
  )
  const { data: configration } = useGetConfigurationQuery('api/Configurations')
  const { data: transportationTravels } = useGetTransportationByIdQuery(
    params.id ? Number(params.id) : skipToken
  )
  const { data: transportationTravelsById } = useGetTransportationByIdQuery(
    params.id ? Number(params.id) : skipToken
  )

  const navigate = useNavigate()
  const onSubmit = async (values: any): Promise<void> => {
    const allValues = [...values.TransportationTravels, ...deletedItems]
    const newValues = allValues.map((ele: any) => {
      const { id, ...rest } = ele
      return rest
    })
    if (mode === 'edit') {
      editTravels({ body: newValues, id: Number(params.id) })
        .then((res: any) => {
          toast({
            title: 'Travels edited successfully',
            status: 'success',
            duration: 1500,
            isClosable: true,
          })
          navigate(`/admin/Transportation`)
        })
        .catch((err: any) => {
          toast({
            title: err.message,
            status: 'error',
            duration: 1500,
            isClosable: true,
          })
        })
    } else {
      addTravels({ body: newValues, id })
        .then((res: any) => {
          toast({
            title: 'Travels created successfully',
            status: 'success',
            duration: 1500,
            isClosable: true,
          })
          navigate(`/admin/Transportation`)
        })
        .catch((err: any) => {
          toast({
            title: err.message,
            status: 'error',
            duration: 1500,
            isClosable: true,
          })
        })
    }
  }
  const validate = (values: any): any => {
    const errors: any = {}

    if (!values.TransportationTravels || values.TransportationTravels.length === 0) {
      errors.TransportationTravels = t('validation required')
    } else {
      values.TransportationTravels.forEach((travel: any, index: number) => {
        const travelErrors: any = {}
        if (!travel.FromCountryId) {
          travelErrors.FromCountryId = t('validation required')
        }
        if (!travel.FromCityId) {
          travelErrors.FromCityId = t('validation required')
        }
        if (!travel.DepartureDateTime) {
          travelErrors.DepartureDateTime = t('validation required')
        }
        if (!travel.ToCityId) {
          travelErrors.ToCityId = t('validation required')
        }
        if (!travel.ArrivalDateTime) {
          travelErrors.ArrivalDateTime = t('validation required')
        }
        if (!travel.Price) {
          travelErrors.Price = t('validation required')
        }
        if (!travel.VehicleTypeId) {
          travelErrors.VehicleTypeId = t('validation required')
        }
        if (!travel.Brand) {
          travelErrors.Brand = t('validation required')
        }
        if (!travel.VehicleNumber) {
          travelErrors.VehicleNumber = t('validation required')
        }
        if (travel.DepartureDateTime && travel.ArrivalDateTime) {
          if (dayjs(travel.DepartureDateTime).isAfter(dayjs(travel.ArrivalDateTime))) {
            travelErrors.ArrivalDateTime = t('Arrival Date must be before Departure Date')
          }
        }
        if (travel.FromCityId === travel.ToCityId) {
          travelErrors.ToCityId = t('must not same City')
        }
        if (Object.keys(travelErrors).length > 0) {
          errors.TransportationTravels = errors.TransportationTravels || []
          errors.TransportationTravels[index] = travelErrors
        }
      })
    }
    return errors
  }
  return (
    <>
      <div className="hotelsGeneral w-full mt-4  ">
        <div className="hotelsGeneralContent">
          <div className="w-full pt-0 pb-0 mb-[-10px] text-black">
            <Form
              onSubmit={onSubmit}
              validate={validate}
              initialValues={
                mode === 'edit'
                  ? {
                      TransportationTravels: transportationTravelsById?.data?.Travels || [],
                    }
                  : {}
              }
              mutators={{ ...arrayMutators }}
              render={({ handleSubmit, form, submitting, pristine, modifiedSinceLastSubmit }) => {
                return (
                  <form onSubmit={handleSubmit} className="w-full flex flex-wrap">
                    <FieldArray name="TransportationTravels">
                      {({ fields }) => (
                        <>
                          {fields.map((name, index) => (
                            <TransportationTravel
                              mode={mode}
                              configration={configration}
                              key={`Travel-${name}`}
                              name={name}
                              fields={fields}
                              id={id}
                              transportationTravelsById={transportationTravelsById}
                              index={index}
                              onRemove={() => {
                                const haveId = fields.value[index].Id
                                const deletedId = fields.value[index]
                                const deletedItem = { ...deletedId, IsDeleted: true }
                                if (haveId) {
                                  setDeletedItems((prev: any) => [...prev, deletedItem])
                                }
                                fields.remove(index)
                              }}
                            />
                          ))}
                          {mode !== 'view' && (
                            <div className="w-full flex justify-end mt-3 me-3 pe-1">
                              <button
                                onClick={() => fields.push({ id: Date.now() })}
                                className="btn-alternative text-lg w-52 cursor-pointer ms-auto"
                                type="button">
                                {t('+ Add Travel')}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </FieldArray>

                    {mode !== 'view' && (
                      <div className=" mt-3 mb-3  ml-auto rtl:ml-0 rtl:mr-auto flex p-1 mr-0 ">
                        <div>
                          <button
                            className="btn-primary text-sm	 rounded-xl cursor-pointer w-28 "
                            type="submit"
                            disabled={submitting || pristine}>
                            {t('Save')}
                          </button>
                        </div>
                        <div className="px-1">
                          <button
                            className="btn-alternative text-sm	 cursor-pointer"
                            type="button"
                            disabled={submitting || (!modifiedSinceLastSubmit && pristine)}
                            onClick={() => {
                              form.reset()
                            }}>
                            {t('Clear All')}
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                )
              }}
            />
          </div>
        </div>
      </div>
      {isLoading && <Loading />}
    </>
  )
}
